/**
 * "Exactly which model makes each picture" (mockup board 1, Task 15) —
 * models × "used for" capabilities, with a "Used by" column showing which
 * action step, vibe override or role actually routes to each model.
 *
 * `usedBy` is server-computed (`withUsedBy`, pos-agents-service
 * admin-ai-catalog.ts) on rows from `GET /agents/admin/ai/catalog`. A model
 * fetched from elsewhere (or an older cached response) may not carry it —
 * `computeUsedBy` below is the client-side fallback, used ONLY when the
 * server field is absent, per the task's instruction to prefer the server
 * value.
 */
import React from 'react';
import { Check } from 'lucide-react';
import type { AiModelDto, AiModelUseDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiVibeDto } from '../../../services/aiControl/rubyAi';
import { USED_FOR_DISPLAY_GROUPS, USED_FOR_LABELS, normalizeUsedFor } from './catalogModel';
import { AI_MODEL_USED_FOR } from './units';
import { Card, Mono } from './ui';

const ALL_CAPS = USED_FOR_DISPLAY_GROUPS.flatMap((g) => g.caps);

/**
 * Client-side fallback for `usedBy`, mirroring the server's `withUsedBy`
 * (admin-ai-catalog.ts) for actions and vibes (roles are not surfaced to
 * this tab, so they are left out of the fallback).
 */
export function computeUsedBy(
  model: Pick<AiModelDto, 'providerId' | 'modelId'>, actions: AiActionDto[], vibes: AiVibeDto[],
): AiModelUseDto[] {
  const uses: AiModelUseDto[] = [];
  const is = (ref?: { providerId?: string; modelId?: string } | null) =>
    !!ref && ref.providerId === model.providerId && ref.modelId === model.modelId;
  for (const a of actions) {
    if (a.steps?.length) {
      for (const s of a.steps) {
        if (is(s)) uses.push({ type: 'action', id: a.actionId, step: s.id, use: 'primary' });
        for (const f of s.fallbacks ?? []) if (is(f)) uses.push({ type: 'action', id: a.actionId, step: s.id, use: 'fallback' });
      }
    } else {
      if (is(a)) uses.push({ type: 'action', id: a.actionId, step: 'main', use: 'primary' });
      if (is({ providerId: a.fallbackProviderId, modelId: a.fallbackModelId })) {
        uses.push({ type: 'action', id: a.actionId, step: 'main', use: 'fallback' });
      }
    }
  }
  for (const v of vibes) {
    if (is(v)) uses.push({ type: 'vibe', id: v.vibeId, use: 'override' });
    for (const [actionId, ref] of Object.entries(v.modelOverrides ?? {})) {
      if (is(ref)) uses.push({ type: 'vibe', id: v.vibeId, actionId, use: 'override' });
    }
  }
  return uses;
}

/** "Single object image · draw (fallback)" style label for one usedBy entry. */
export function usedByLabel(use: AiModelUseDto, actions: AiActionDto[], vibes: AiVibeDto[]): string {
  if (use.type === 'vibe') {
    const vibe = vibes.find((v) => v.vibeId === use.id);
    const label = vibe?.label ?? use.id;
    if (use.actionId) {
      const action = actions.find((a) => a.actionId === use.actionId);
      return `${action?.label ?? use.actionId} vibe override · ${label}`;
    }
    return `${label} vibe override`;
  }
  if (use.type === 'role') return `${use.id} role (legacy)${use.use === 'fallback' ? ' (fallback)' : ''}`;
  const action = actions.find((a) => a.actionId === use.id);
  const label = action?.label ?? use.id;
  const step = use.step && use.step !== 'main' ? ` · ${use.step}` : '';
  const fb = use.use === 'fallback' ? ' (fallback)' : '';
  return `${label}${step}${fb}`;
}

/** The entries to show for one model — server `usedBy` preferred, else the client-side fallback. */
export function usedByFor(model: AiModelDto, actions: AiActionDto[], vibes: AiVibeDto[]): AiModelUseDto[] {
  return model.usedBy ?? computeUsedBy(model, actions, vibes);
}

const CapabilityGrid: React.FC<{ models: AiModelDto[]; actions: AiActionDto[]; vibes: AiVibeDto[] }> = ({
  models, actions, vibes,
}) => {
  const rows = [...models].sort((a, b) => a.providerId.localeCompare(b.providerId) || a.label.localeCompare(b.label));

  return (
    <Card className="overflow-hidden">
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-stone-600">No models registered yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-600">
                <th className="text-left font-semibold px-4 py-3">Model</th>
                {ALL_CAPS.map((c) => (
                  <th key={c} className="text-center font-semibold px-2 py-3 whitespace-nowrap" title={USED_FOR_LABELS[c] ?? c}>
                    {USED_FOR_LABELS[c] ?? c}
                  </th>
                ))}
                <th className="text-left font-semibold px-3 py-3">Used by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const { usedFor } = normalizeUsedFor(m.capabilities, AI_MODEL_USED_FOR);
                const uses = usedByFor(m, actions, vibes);
                return (
                  <tr key={`${m.providerId}#${m.modelId}`} className="border-b border-stone-100 last:border-0 align-top">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-stone-900">{m.label}</p>
                      <Mono className="text-stone-500">{m.providerId}/{m.modelId}</Mono>
                    </td>
                    {ALL_CAPS.map((c) => (
                      <td key={c} className="px-2 py-3 text-center">
                        {usedFor.includes(c)
                          ? <Check size={16} className="inline text-emerald-600" aria-label={`${USED_FOR_LABELS[c] ?? c}: yes`} />
                          : <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-300" aria-label={`${USED_FOR_LABELS[c] ?? c}: no`} />}
                      </td>
                    ))}
                    <td className="px-3 py-3 text-stone-700">
                      {uses.length === 0
                        ? <span className="text-stone-400">Not used by anything today</span>
                        : <ul className="space-y-0.5">{uses.map((u, i) => <li key={i}>{usedByLabel(u, actions, vibes)}</li>)}</ul>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default CapabilityGrid;
